import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import {
    Formik,
    Form,
    Field,
    FieldArray
} from "formik";
import { toast } from "react-toastify";

import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    makeStyles,
    TextField
} from "@material-ui/core";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import AttachFileIcon from "@material-ui/icons/AttachFile";

import { green } from "@material-ui/core/colors";

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Box from "@material-ui/core/Box";
import Collapse from "@material-ui/core/Collapse";
import CloseIcon from "@material-ui/icons/Close";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import FolderIcon from "@material-ui/icons/Folder";
import { getStandardButtonStyles } from "../../styles/standardButtons";
import { AuthContext } from "../../context/Auth/AuthContext";

const useStyles = makeStyles(theme => ({
    ...getStandardButtonStyles(theme),
    dialogPaper: {
        backgroundColor: theme.palette.type === "dark" ? "#1E2126" : "#FFFFFF",
        borderRadius: 16,
        border: `1px solid ${theme.palette.primary.main}40`,
        boxShadow: "0 16px 48px rgba(0, 0, 0, 0.45)",
        color: theme.palette.type === "dark" ? "#E2E8F0" : theme.palette.text.primary,
        overflow: "hidden",
    },
    dialogHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.08)"
        }`,
        "& h2": {
            display: "flex",
            alignItems: "center",
            fontSize: "1.15rem",
            fontWeight: 600,
            margin: 0,
            color: theme.palette.type === "dark" ? "#FFFFFF" : theme.palette.text.primary,
        },
    },
    helpBox: {
        backgroundColor:
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.08)" : "rgba(0, 180, 216, 0.05)",
        borderBottom: `1px solid ${
            theme.palette.type === "dark" ? "rgba(0, 180, 216, 0.2)" : "rgba(0, 180, 216, 0.15)"
        }`,
        padding: theme.spacing(1.5, 3),
    },
    root: {
        display: "flex",
        flexWrap: "wrap",
        gap: 4
    },
    multFieldLine: {
        display: "flex",
        "& > *:not(:last-child)": {
            marginRight: theme.spacing(1),
        },
    },
    textField: {
        marginRight: theme.spacing(1),
        flex: 1,
    },

    extraAttr: {
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
    },

    btnWrapper: {
        position: "relative",
    },

    buttonProgress: {
        color: green[500],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    formControl: {
        margin: theme.spacing(1),
        minWidth: 2000,
    },
    colorAdorment: {
        width: 20,
        height: 20,
    },
}));

const getFileUrl = (path) => {
    if (!path) return "";
    const backendUrl = process.env.REACT_APP_BACKEND_URL || "http://localhost:8080";

    if (path.startsWith("/")) {
        return `${backendUrl}${path}`;
    }

    if (path.includes("localhost")) {
        try {
            const urlObj = new URL(path);
            return `${backendUrl}${urlObj.pathname}`;
        } catch (e) {
            return path.replace("http://localhost:8080", backendUrl).replace("http://localhost:3000", backendUrl);
        }
    }

    return path;
};



const FilesModal = ({ open, onClose, fileListId, reload }) => {
    const classes = useStyles();
    const { user } = useContext(AuthContext);
    const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

    const [files, setFiles] = useState([]);
    const [selectedFileNames, setSelectedFileNames] = useState([]);
    const [showHelp, setShowHelp] = useState(false);


    const initialState = {
        name: "",
        message: "",
        options: [{ name: "", path: "", mediaType: "" }],
    };

    const [fileList, setFileList] = useState(initialState);

    const FileListSchema = Yup.object().shape({
        name: Yup.string()
            .min(3, i18n.t("fileModal.validation.tooShort"))
            .required(i18n.t("fileModal.validation.required")),
        // message: Yup.string()
        //     .required(i18n.t("fileModal.validation.required"))
    });

    useEffect(() => {
        try {
            (async () => {
                if (!fileListId) return;

                const { data } = await api.get(`/files/${fileListId}`);
                setFileList(data);
            })()
        } catch (err) {
            toastError(err);
        }
    }, [fileListId, open]);

    const handleClose = () => {
        setFileList(initialState);
        setFiles([]);
        setShowHelp(false);
        onClose();
    };

    const handleSaveFileList = async (values) => {

        const uploadFiles = async (options, filesOptions, id) => {
            const formData = new FormData();
            formData.append("fileId", id);
            formData.append("typeArch", "fileList")
            filesOptions.forEach((fileOption, index) => {
                if (fileOption.file) {
                    formData.append("files", fileOption.file);
                    formData.append("mediaType", fileOption.file.type)
                    formData.append("name", options[index].name);
                    formData.append("id", options[index].id);
                }
            });

            try {
                const { data } = await api.post(`/files/uploadList/${id}`, formData);
                setFiles([]);
                return data;
            } catch (err) {
                toastError(err);
            }
            return null;
        }

        const fileData = { ...values, message: values.message || "File List", userId: user.id };

        try {
            if (fileListId) {
                const { data } = await api.put(`/files/${fileListId}`, fileData)
                if (data.options.length > 0)
                    // console.log(values.options)
                    uploadFiles(data.options, values.options, fileListId)
            } else {
                const { data } = await api.post("/files", fileData);
                if (data.options.length > 0)
                    uploadFiles(data.options, values.options, data.id)
            }
            toast.success(i18n.t("fileModal.success"));
            if (typeof reload == 'function') {
                reload();
            }
        } catch (err) {
            toastError(err);
        }
        handleClose();
    };

    return (
        <div className={classes.root}>
            <Dialog
                open={open}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                scroll="paper"
                PaperProps={{
                    className: classes.dialogPaper,
                }}>
                <div className={classes.dialogHeader}>
                    <h2>
                        <FolderIcon style={{ marginRight: 10, fontSize: 22, color: "#00b4d8" }} />
                        {fileListId ? `${i18n.t("fileModal.title.edit")}` : `${i18n.t("fileModal.title.add")}`}
                    </h2>
                    <Box display="flex" alignItems="center">
                        <IconButton
                            size="small"
                            onClick={() => setShowHelp(prev => !prev)}
                            title="Ayuda / Información"
                            style={{ marginRight: 8, color: showHelp ? "#00b4d8" : "inherit" }}
                        >
                            <HelpOutlineIcon fontSize="small" />
                        </IconButton>
                        <Typography
                            variant="caption"
                            style={{ opacity: 0.6, marginRight: 12, fontWeight: 500 }}
                        >
                            BotMix | CRM
                        </Typography>
                        <IconButton size="small" onClick={handleClose} aria-label="Cerrar">
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </Box>
                </div>

                <Collapse in={showHelp}>
                    <Box className={classes.helpBox}>
                        <Typography
                            variant="body2"
                            style={{
                                color: "#00b4d8",
                                lineHeight: 1.5,
                            }}
                        >
                            {i18n.t("fileModal.helpSummary")}
                        </Typography>
                    </Box>
                </Collapse>
                <Formik
                    initialValues={fileList}
                    enableReinitialize={true}
                    validationSchema={FileListSchema}
                    onSubmit={(values, actions) => {
                        setTimeout(() => {
                            handleSaveFileList(values);
                            actions.setSubmitting(false);
                        }, 400);
                    }}
                >
                    {({ touched, errors, isSubmitting, values, setFieldValue }) => (
                        <Form>
                            <DialogContent dividers>
                                <div className={classes.multFieldLine}>
                                    <Field
                                        as={TextField}
                                        label={i18n.t("fileModal.form.name")}
                                        name="name"
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name && errors.name}
                                        variant="outlined"
                                        margin="dense"
                                        fullWidth
                                        disabled
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </div>
                                <br />
                                <Typography
                                    style={{ marginBottom: 8, marginTop: 12 }}
                                    variant="subtitle1"
                                >
                                    {i18n.t("fileModal.form.fileOptions")}
                                </Typography>

                                <FieldArray name="options">
                                    {({ push, remove }) => (
                                        <>
                                            {values.options &&
                                                values.options.length > 0 &&
                                                values.options.map((info, index) => (
                                                    <div
                                                        className={classes.extraAttr}
                                                        key={`${index}-info`}
                                                    >
                                                        <Grid container spacing={0}>
                                                            <Grid xs={6} md={10} item>
                                                                <Field
                                                                    as={TextField}
                                                                    label={i18n.t("fileModal.form.extraName")}
                                                                    name={`options[${index}].name`}
                                                                    variant="outlined"
                                                                    margin="dense"
                                                                    multiline
                                                                    fullWidth
                                                                    minRows={2}
                                                                    className={classes.textField}
                                                                />
                                                            </Grid>
                                                            <Grid xs={2} md={2} item style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                                                                <input
                                                                    type="file"
                                                                    onChange={(e) => {
                                                                        const selectedFile = e.target.files[0];
                                                                        if (selectedFile && selectedFile.size > MAX_FILE_SIZE) {
                                                                            toast.error(i18n.t("fileModal.validation.maxSize") || "El archivo es demasiado grande (Máx 5MB)");
                                                                            e.target.value = null;
                                                                            return;
                                                                        }
                                                                        if (selectedFile) {
                                                                            setFieldValue(`options[${index}].file`, selectedFile);
                                                                            setFieldValue(`options[${index}].name`, selectedFile.name);

                                                                            // Auto-fill list name if empty or first item
                                                                            if (!values.name || index === 0) {
                                                                                setFieldValue("name", selectedFile.name);
                                                                            }

                                                                            const updatedFileNames = [...selectedFileNames];
                                                                            updatedFileNames[index] = selectedFile.name;
                                                                            setSelectedFileNames(updatedFileNames);
                                                                        }
                                                                    }}
                                                                    style={{ display: 'none' }}
                                                                    name={`options[${index}].file`}
                                                                    id={`file-upload-${index}`}
                                                                />
                                                                <label htmlFor={`file-upload-${index}`}>
                                                                    <IconButton component="span">
                                                                        <AttachFileIcon />
                                                                    </IconButton>
                                                                </label>
                                                            </Grid>
                                                            <Grid xs={12} md={12} item>
                                                                {/* Preview Section */}
                                                                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                                                                    {info.file && info.file.type.startsWith('image/') && (
                                                                        <img
                                                                            src={URL.createObjectURL(info.file)}
                                                                            alt="Preview"
                                                                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                                                        />
                                                                    )}
                                                                    {info.file && info.file.type.startsWith('video/') && (
                                                                        <video
                                                                            src={URL.createObjectURL(info.file)}
                                                                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                                                        />
                                                                    )}
                                                                    {/* For existing files (edit mode) */}
                                                                    {!info.file && info.path && (info.path.endsWith('.jpg') || info.path.endsWith('.png') || info.path.endsWith('.jpeg')) && (
                                                                        <img
                                                                            src={getFileUrl(info.path)}
                                                                            alt="Preview"
                                                                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                                                        />
                                                                    )}
                                                                    {!info.file && info.path && (info.path.endsWith('.mp4')) && (
                                                                        <video
                                                                            src={getFileUrl(info.path)}
                                                                            style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
                                                                        />
                                                                    )}

                                                                    <Typography variant="caption" color="textSecondary">
                                                                        {info.file ? info.file.name : (info.path ? info.path.split('/').pop() : selectedFileNames[index])}
                                                                    </Typography>
                                                                </div>
                                                            </Grid>
                                                        </Grid>
                                                    </div>

                                                ))}
                                            

                                        </>
                                    )}
                                </FieldArray>
                            </DialogContent>
                            <DialogActions style={{ padding: "16px 24px" }}>
                                <Button
                                    onClick={handleClose}
                                    disabled={isSubmitting}
                                    className={classes.dangerButton}
                                >
                                    {i18n.t("fileModal.buttons.cancel")}
                                </Button>
                                <Button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={classes.applyButton}
                                >
                                    {fileListId
                                        ? `${i18n.t("fileModal.buttons.okEdit")}`
                                        : `${i18n.t("fileModal.buttons.okAdd")}`}
                                    {isSubmitting && (
                                        <CircularProgress
                                            size={20}
                                            style={{ color: "#fff", marginLeft: 8 }}
                                        />
                                    )}
                                </Button>
                            </DialogActions>
                        </Form>
                    )}
                </Formik>
            </Dialog>
        </div>
    );
};

export default FilesModal;
